import { Link, useNavigate } from "react-router-dom";
import { LogOut, Menu, Building2, Users, LayoutDashboard, GraduationCap, BookOpen, ShieldCheck, Settings, UserRound, FileText, Smartphone } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function AppHeader() {
  const { user, account, logout } = useAuth();
  const nav = useNavigate();
  const isAdmin = !!user && (user.role === "owner" || user.role === "content_editor");
  const isEditor = user?.role === "content_editor";
  const displayName = user?.name || [user?.nombre_pila, user?.apellido_paterno].filter(Boolean).join(" ");

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-40 bg-[#FBF9F5]/85 backdrop-blur-md border-b border-[#E2DCD2]"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to={isEditor ? "/app/admin/copiloto" : "/app"} className="flex items-center gap-2.5" data-testid="header-home-link">
          <div className="leading-none">
            <div className="font-heading font-extrabold text-slate-900 text-lg sm:text-xl tracking-tight">Co-Legal</div>
          </div>
        </Link>

        <nav className={isEditor ? "hidden" : "hidden md:flex items-center gap-1"}>
          <Link to="/app" className="px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-[#F3EFE6]" data-testid="nav-dashboard">
            <LayoutDashboard className="w-4 h-4 inline mr-1.5" /> Operaciones
          </Link>
          <Link to="/app/personas" className="px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-[#F3EFE6]" data-testid="nav-personas">
            <Users className="w-4 h-4 inline mr-1.5" /> Personas
          </Link>
          <Link to="/app/inmuebles" className="px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-[#F3EFE6]" data-testid="nav-inmuebles">
            <Building2 className="w-4 h-4 inline mr-1.5" /> Inmuebles
          </Link>
          <Link to="/app/capacitacion" className="px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-[#F3EFE6]" data-testid="nav-capacitacion">
            <GraduationCap className="w-4 h-4 inline mr-1.5" /> Capacitación
          </Link>
        </nav>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu-btn" className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#E2DCD2] hover:bg-white transition-colors">
              <div className="w-7 h-7 rounded-full bg-[#2A5C55] text-white text-xs flex items-center justify-center font-semibold">
                {(displayName || user?.phone || "U").slice(0, 1).toUpperCase()}
              </div>
              <span className="hidden sm:inline text-sm font-medium text-slate-700 max-w-[140px] truncate">
                {displayName || user?.phone}
              </span>
              <Menu className="w-4 h-4 text-slate-500" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <div className="px-2 py-2 text-xs text-slate-500">
              <div className="font-semibold text-slate-800 text-sm">{displayName || "Asesor"}</div>
              <div>{user?.phone}</div>
              {account?.name && <div className="mt-1">Cuenta: <span className="text-slate-700">{account.name}</span></div>}
            </div>
            {!isEditor && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/app/perfil")} data-testid="menu-mi-perfil">
                  <UserRound className="w-4 h-4 mr-2" /> Mi perfil
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/app/personas")} data-testid="menu-personas">
                  <Users className="w-4 h-4 mr-2" /> Personas
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/app/inmuebles")} data-testid="menu-inmuebles">
                  <Building2 className="w-4 h-4 mr-2" /> Inmuebles
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/app/capacitacion")} data-testid="menu-capacitacion">
                  <GraduationCap className="w-4 h-4 mr-2" /> Capacitación
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/app/instalar")} data-testid="menu-instalar">
                  <Smartphone className="w-4 h-4 mr-2" /> Instalar en mi celular
                </DropdownMenuItem>
              </>
            )}
            {isAdmin && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/app/admin/copiloto")} data-testid="menu-admin-copiloto">
                  <BookOpen className="w-4 h-4 mr-2" /> Contenido Copiloto y FAQ
                </DropdownMenuItem>
                {user?.role === "owner" && (
                  <>
                    <DropdownMenuItem onClick={() => nav("/app/admin/usuarios")} data-testid="menu-admin-usuarios">
                      <ShieldCheck className="w-4 h-4 mr-2" /> Usuarios y permisos
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => nav("/app/admin/plantillas")} data-testid="menu-admin-plantillas">
                      <FileText className="w-4 h-4 mr-2" /> Plantillas de contratos
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => nav("/app/admin/ajustes")} data-testid="menu-admin-ajustes">
                      <Settings className="w-4 h-4 mr-2" /> Ajustes (caducidad de links)
                    </DropdownMenuItem>
                  </>
                )}
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} data-testid="menu-logout">
              <LogOut className="w-4 h-4 mr-2" /> Cerrar sesión
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
